import { useState, useCallback } from 'react'
import { ClaimSearchPage } from './Claim/Search/ClaimSearchPage'
import { ClaimDetailsPage } from './Claim/ClaimDetailsPage'
import { theme, cssVariablesResolver } from '../theme'
import { MantineProvider, Skeleton, Stack, Button } from '@mantine/core'
import { useOfficeTheme } from '../hooks/useOfficeTheme'
import { useEmailClaimDetection } from '../hooks/useEmailClaimDetection'
import { useFeatureFlags } from '../hooks/useFeatureFlags'
import { useRepliconUserStatus } from '../hooks/useRepliconUserStatus'
import { StatusMessage } from './Common/StatusMessage'
import '@mantine/core/styles.css'

// Shared by the detection failure and the feature-flags failure: both leave the pane
// unable to show the right thing, so both stop at the same message plus a manual retry.
const renderErrorState = (message: string | undefined, onRetry: () => void) => (
  <Stack gap="md" p="md" align="center" justify="center" mih="100vh">
    <StatusMessage status="error" ta="center">
      {message}
    </StatusMessage>
    <Button variant="filled" size="xs" onClick={onRetry}>
      Retry
    </Button>
  </Stack>
)

const App: React.FC = () => {
  const [selectedClaim, setSelectedClaim] = useState<string | null>(null)
  const colorScheme = useOfficeTheme()
  const detection = useEmailClaimDetection()
  const {
    enableContactsTab,
    enableTaskTab,
    enableTimeEntry,
    enableFileNote,
    errorMessage: flagsError,
    retry: retryFlags,
  } = useFeatureFlags()
  // Only asked once EnableTimeEntry is on. The flag decides whether the Time Entry tab shows;
  // this decides what it holds — the booking form, or a link to Vision.
  const { isRepliconUser, loading: repliconLoading } = useRepliconUserStatus(enableTimeEntry)

  // Derive the active claim: user-selected claim takes priority, then auto-detected
  const activeClaim = selectedClaim ?? (detection.status === 'found' ? detection.claimNumber : null)

  const handleConnect = useCallback((claim: { claimNumber: string } | null) => {
    setSelectedClaim(claim?.claimNumber ?? null)
  }, [])

  const handleBack = () => {
    handleConnect(null)
    detection.reset()
  }

  const detectionMessage =
    detection.status === 'not-found' && detection.detectedReference
      ? `Reference '${detection.detectedReference}' detected but no matching claim found.`
      : undefined

  const isDetecting = detection.status === 'idle' || detection.status === 'detecting'

  const renderContent = () => {
    // Every flag fails closed, so a failed settings call would silently hide gated
    // features. Stop here instead and let the user retry.
    if (flagsError) {
      return renderErrorState(flagsError, retryFlags)
    }
    if (activeClaim) {
      return (
        <ClaimDetailsPage
          claimNumber={activeClaim}
          onBack={handleBack}
          showContacts={enableContactsTab}
          showTasks={enableTaskTab}
          showTimeEntry={enableTimeEntry}
          isRepliconUser={isRepliconUser}
          repliconLoading={repliconLoading}
          showFileNote={enableFileNote}
        />
      )
    }
    if (isDetecting) {
      return (
        <Stack gap="md" p="md" mih="100vh">
          <Skeleton height={30} width="60%" />
          <Skeleton height={36} />
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} height={80} />
          ))}
        </Stack>
      )
    }

    if (detection.status === 'error') {
      return renderErrorState(detection.errorMessage, detection.retry)
    }

    return (
      <ClaimSearchPage
        onConnect={handleConnect}
        connectedClaimNumber={activeClaim ?? undefined}
        highlightClaimNumber={detection.highlightClaimNumber ?? undefined}
        detectionMessage={detectionMessage}
      />
    )
  }

  return (
    <MantineProvider theme={theme} cssVariablesResolver={cssVariablesResolver} forceColorScheme={colorScheme}>
      {renderContent()}
    </MantineProvider>
  )
}

export default App
